import React, { FC, useEffect, useMemo, useState } from "react";
import {
	Box,
	Flex,
	Text,
	Tab,
	Tabs,
	TabPanel,
	TabPanels,
	TabList,
	Button,
	ModalBody,
	ModalFooter,
	Table,
	Td,
	Thead,
	Tr,
} from "@chakra-ui/react";
import { Tbody } from "@familyzone/component-library";
import { useToast } from "@/components/ui/fzcl/Toast";
import { SplitModal } from "./SplitModal";
import { ClassroomDetails, ClassroomOverviewDTO, ModifyClassDTO } from "@/stores/classroomStore_I";
import { UserInfo } from "@/js/api/authApi";
import { getClassroomDetails } from "@/js/api/classApi";
import useStore from "@/stores/index";
import { compareChangesInArrays } from "@/js/utils/arrayUtils";
import { getClassroomStore } from "@/stores/classroomStore";
import { ClassCombinationResponse, GetClassCombinations, UpdateClassroomDetails } from "@/js/api/combineClassesApi";
import { getProviderDisplayText2 } from "@/js/utils/providerUtils";
import { colours, toasts } from "@/utils/themeUtils";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { Notice } from "@/components/Notice";
import { ClasswizeModal } from "@/components/ui/Modal";
import { TableContainer, Th } from "@/components/ui/Table";

type props = {
	open: boolean;
	onClose: () => void;
	classroom: ClassroomOverviewDTO;
};

const tabs = ["Classes"];

const columns = [
	{
		headerText: "Class name",
		columnName: "className",
	},
	{
		headerText: "Provider",
		columnName: "provider",
	},
];

const buttonTextSplit = "Split Classes";
const buttonTextDelete = "Delete Class";
const buttonTextSave = "Save";
const descriptiveTextClasses = "This class consists of";
const successNotificationSaveStudent = "Student membership has been successfully updated for class";
const errorNotificationTitleSaveStudent = "There was an error updating the student membership for class";
const errorNotificationTextSaveStudent = "Please go to class settings and try again";
const notificationTextNotAvailable =
	"The classes you have combined within this class are no longer available following the most recent sync";
const successNotification = "is deleted successfully";
const errorNotificationTitle = "There was an error while deleting a combined class";
const errorNotificationText = "Please go to class settings and try again";

export const SettingsModal: FC<props> = ({ open, onClose, classroom }) => {
	const { successToast, errorToast } = useToast();
	const [tabIndex, setTabIndex] = useState(0);
	const [splitModalOpen, setSplitModalOpen] = useState(false);
	const [students, setStudents] = useState<UserInfo[]>([]);
	const [teachers, setTeachers] = useState<UserInfo[]>([]);
	const [stagedStudents, setStagedStudents] = useState<UserInfo[]>([]);
	const [classCombination, setClassCombination] = useState<ClassCombinationResponse[]>([]);
	const [isLoaded, setIsLoaded] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const studentsNotChanged = useMemo(
		() => students.length === 0 || compareChangesInArrays(stagedStudents, students) === 0,
		[students, stagedStudents]
	);

	const classNotAvailable = useMemo(() => isLoaded && classCombination.length === 0, [isLoaded, classCombination]);

	const handleDelete = async () => {
		const { handleDeleteClassroom, fetchClassrooms } = getClassroomStore(useStore.getState());

		try {
			setIsSaving(true);

			await handleDeleteClassroom(classroom.stableIdV1);
			await fetchClassrooms();

			successToast({
				title: `${title} ${successNotification}`,
				position: toasts.position,
				isClosable: true,
			});
		} catch (error) {
			console.error("Error deleting classroom:", error);
			errorToast({
				title: `${errorNotificationTitle} ${title}`,
				description: errorNotificationText,
				position: toasts.position,
				isClosable: true,
			});
		} finally {
			setIsSaving(false);
			onClose();
		}
	};

	const handleSave = async () => {
		const updatedClassroom: ModifyClassDTO = {
			group: classroom.sourceId,
			groupId: classroom.id,
			classroomUsers: stagedStudents.map((user) => user.username),
			classroomUserIds: stagedStudents.map((user) => user.id),
			classroomOwners: teachers.map((user) => user.username),
			classroomOwnerIds: teachers.map((user) => user.id),
		};

		const { updateClassroomDataV2 } = getClassroomStore(useStore.getState());

		try {
			setIsSaving(true);

			await UpdateClassroomDetails(updatedClassroom);
			updateClassroomDataV2(updatedClassroom, true);

			successToast({
				title: `${successNotificationSaveStudent} ${title}`,
				position: toasts.position,
				isClosable: true,
			});
		} catch (error) {
			console.error("Error saving classroom:", error);
			errorToast({
				title: `${errorNotificationTitleSaveStudent} ${title}`,
				description: errorNotificationTextSaveStudent,
				position: toasts.position,
				isClosable: true,
			});
		} finally {
			setIsSaving(false);
			onClose();
		}
	};

	const onCloseSplitModal = () => {
		setSplitModalOpen(false);
		if (isSaving) onClose();
	};

	const tableClassDataMap = (item: ClassCombinationResponse, index: number) => (
		<Tr key={index}>
			<Td>
				<Text fontSize="md">{item.displayName || item.sourceName}</Text>
			</Td>
			<Td>
				<Text fontSize="md">{getProviderDisplayText2(item.sourceType, item.classType)}</Text>
			</Td>
		</Tr>
	);

	const title = useMemo(() => classroom.personalisation?.displayName ?? "", [classroom]);

	useEffect(() => {
		void (async () => {
			setIsLoaded(false);

			// Get class combinations
			const { getTeacherStableIdV1 } = getClassroomStore(useStore.getState());
			const classCombinations = await GetClassCombinations(classroom.stableIdV1, getTeacherStableIdV1());
			setClassCombination(classCombinations);

			// Get class details
			const classroomDetail = (await getClassroomDetails(
				classroom.id,
				true,
				false,
				false,
				false,
				classroom.type,
				classroom.sourceId,
				useUserSessionStore.getState().getTeacherId()
			)) as {
				classroom: ClassroomDetails;
			};
			setStudents(classroomDetail.classroom.students || []);
			setStagedStudents(classroomDetail.classroom.students || []);
			setTeachers(classroomDetail.classroom.teachers || []);

			setIsLoaded(true);
		})();
	}, [classroom]);

	const renderButtons = () => {
		if (tabIndex === 0) {
			if (classNotAvailable) {
				return (
					<Button onClick={handleDelete} data-testid="modal-next-btn" isDisabled={!isLoaded} isLoading={isSaving}>
						{buttonTextDelete}
					</Button>
				);
			}

			return (
				<Button onClick={() => setSplitModalOpen(true)} data-testid="modal-next-btn" isDisabled={!isLoaded} isLoading={isSaving}>
					{buttonTextSplit}
				</Button>
			);
		}

		return (
			<Button onClick={handleSave} data-testid="modal-next-btn" isDisabled={!isLoaded || studentsNotChanged} isLoading={isSaving}>
				{buttonTextSave}
			</Button>
		);
	};

	return (
		<>
			<ClasswizeModal isOpen={open} onClose={onClose} title={title} size="2xl">
				<ModalBody>
					<Tabs onChange={(index) => setTabIndex(index)}>
						<TabList>
							{tabs.map((tab) => (
								<Tab key={tab}>{tab}</Tab>
							))}
						</TabList>

						<TabPanels>
							<TabPanel>
								<Flex direction="column">
									<Flex>
										<Box color={colours.navy[800]} fontFamily="Roboto" fontSize="14px" fontWeight={400} lineHeight="20px">
											{descriptiveTextClasses}
										</Box>
									</Flex>
									<Flex mt="16px" direction="row">
										<TableContainer>
											<Table variant="qoriaStriped">
												<Thead>
													<Tr>
														{columns.map(({ headerText, columnName }) => (
															<Th key={columnName} fontSize={"sm"}>
																{headerText}
															</Th>
														))}
													</Tr>
												</Thead>
												<Tbody loaded={isLoaded}>{classCombination.map(tableClassDataMap)}</Tbody>
											</Table>
										</TableContainer>
									</Flex>
								</Flex>
							</TabPanel>
						</TabPanels>
					</Tabs>
					{classNotAvailable && <Notice status="info" children={notificationTextNotAvailable} />}
				</ModalBody>

				<ModalFooter>
					<Flex direction="row-reverse">{renderButtons()}</Flex>
				</ModalFooter>
			</ClasswizeModal>
			<SplitModal
				open={splitModalOpen}
				onClose={onCloseSplitModal}
				classroom={classroom}
				classCombination={classCombination}
				isSaving={isSaving}
				setIsSaving={setIsSaving}
			/>
		</>
	);
};
